import React, { useEffect, useState } from "react";
import {
    Wallet,
    Fingerprint,
    CheckCircle2,
    Lightbulb,
    Wifi,
    Phone,
    Droplets,
    MessageSquareReply,
} from "lucide-react";
import { useClara } from "../contexts/ClaraContext";
import { useData } from "../contexts/DataContext";
import { BILLS } from "../data/sampleData";
import VoiceReply from "../components/VoiceReply";

const ICONS = {
    electric: Lightbulb,
    internet: Wifi,
    phone: Phone,
    water: Droplets,
};

export const PayBills = () => {
    const { speak } = useClara();
    const { primaryContact, addReply, paidBills, markBillPaid } = useData();
    const [confirmingId, setConfirmingId] = useState(null);
    const [receiptingBill, setReceiptingBill] = useState(null);

    useEffect(() => {
        speak("Pay Bills. Tap any bill to pay it with your fingerprint.");
    }, [speak]);

    const handlePay = (bill) => {
        speak(
            `Paying ${bill.name} bill, ${bill.amount} dollars. Step one: please confirm with your fingerprint.`,
        );
        setConfirmingId(bill.id);
    };

    const confirmFingerprint = (bill) => {
        speak(
            `Thank you. Your ${bill.name} bill of ${bill.amount} dollars has been paid. A receipt is in your messages.`,
        );
        markBillPaid(bill.id);
        setConfirmingId(null);
    };

    return (
        <div className="space-y-3" data-testid="bills-list">
            {BILLS.map((bill) => {
                const Icon = ICONS[bill.id] || Wallet;
                const paid = paidBills.includes(bill.id);
                const confirming = confirmingId === bill.id;
                return (
                    <article
                        key={bill.id}
                        data-testid={`bill-${bill.id}`}
                        className="bg-white rounded-2xl p-4 border border-[#0A3D7A]/10 shadow-[0_2px_12px_rgba(10,61,122,0.06)]"
                    >
                        <div className="flex items-center gap-3">
                            <span
                                aria-hidden="true"
                                className="shrink-0 w-12 h-12 rounded-2xl bg-[#F0EAD6] flex items-center justify-center"
                            >
                                <Icon
                                    className="w-6 h-6 text-[#7A5A0A]"
                                    strokeWidth={2.2}
                                />
                            </span>
                            <div className="flex-1 min-w-0">
                                <p className="font-display font-bold text-lg text-[#1A1A2E] leading-tight">
                                    {bill.name}
                                </p>
                                <p className="text-sm text-[#5A5A6E]">
                                    {bill.provider} · {bill.due}
                                </p>
                            </div>
                            <p
                                className="font-display font-bold text-xl text-[#0A3D7A] shrink-0"
                                aria-label={`${bill.amount} dollars`}
                            >
                                ${bill.amount}
                            </p>
                        </div>

                        {!confirming && !paid && (
                            <button
                                type="button"
                                onClick={() => handlePay(bill)}
                                data-testid={`pay-${bill.id}-btn`}
                                aria-label={`Pay ${bill.name} bill, ${bill.amount} dollars`}
                                className="mt-4 w-full min-h-[52px] rounded-2xl bg-[#0A3D7A] hover:bg-[#082F5E] text-white text-base font-bold flex items-center justify-center gap-2 transition-colors"
                            >
                                Pay ${bill.amount}
                            </button>
                        )}

                        {confirming && (
                            <div
                                className="mt-4 rounded-2xl bg-[#0A3D7A] text-white p-4 fade-in-up"
                                data-testid={`bill-confirm-${bill.id}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="relative shrink-0 w-14 h-14 rounded-full bg-[#B8860B] flex items-center justify-center">
                                        <span
                                            aria-hidden="true"
                                            className="absolute inset-0 rounded-full bg-[#B8860B]/60 pulse-ring"
                                        />
                                        <Fingerprint
                                            className="relative w-7 h-7 text-[#1A1A2E]"
                                            strokeWidth={2.5}
                                        />
                                    </div>
                                    <div>
                                        <p className="font-bold">
                                            Confirm with fingerprint
                                        </p>
                                        <p className="text-sm text-white/85">
                                            Place your finger on the sensor.
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-4 flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => confirmFingerprint(bill)}
                                        data-testid={`bill-confirm-fingerprint-${bill.id}`}
                                        className="flex-1 min-h-[48px] rounded-xl bg-white text-[#0A3D7A] font-bold hover:bg-[#FFF8F0] transition-colors"
                                    >
                                        Simulate fingerprint
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setConfirmingId(null);
                                            speak("Payment cancelled.");
                                        }}
                                        data-testid={`bill-cancel-${bill.id}`}
                                        className="px-4 min-h-[48px] rounded-xl border-2 border-white/40 text-white font-semibold hover:bg-white/10 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        {paid && (
                            <div
                                className="mt-4 rounded-2xl bg-[#E5F1EB] border border-[#0A5C36]/20 p-3 flex items-center gap-2 text-[#0A5C36] fade-in-up"
                                data-testid={`bill-paid-${bill.id}`}
                            >
                                <CheckCircle2
                                    className="w-5 h-5"
                                    aria-hidden="true"
                                />
                                <p className="font-semibold flex-1">
                                    Paid · receipt saved
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setReceiptingBill(bill)}
                                    data-testid={`send-receipt-${bill.id}-btn`}
                                    aria-label={`Send receipt for ${bill.name} bill to ${primaryContact?.name || "your contact"} with a voice note`}
                                    className="ml-1 inline-flex items-center gap-1.5 rounded-xl bg-white border-2 border-[#0A5C36] text-[#0A5C36] text-xs font-bold uppercase tracking-wider px-3 py-1.5 hover:bg-[#0A5C36] hover:text-white transition-colors"
                                >
                                    <MessageSquareReply className="w-3.5 h-3.5" aria-hidden="true" />
                                    Send receipt
                                </button>
                            </div>
                        )}
                    </article>
                );
            })}

            {receiptingBill && (
                <VoiceReply
                    recipient={primaryContact?.name || "Tom"}
                    threadId={`receipt-${receiptingBill.id}`}
                    onSent={(p) =>
                        addReply(
                            `receipt-${receiptingBill.id}`,
                            `[${receiptingBill.name} · $${receiptingBill.amount}] ${p.text}`,
                        )
                    }
                    onClose={() => setReceiptingBill(null)}
                />
            )}
        </div>
    );
};

export default PayBills;
